/**
 * Workspace Store - Zustand State Management
 * 
 * Manages workspace state for multi-context financial management.
 * Workspaces allow users to organize data into separate contexts:
 * - Personal finances
 * - Business/Auto-entrepreneur
 * - Associations
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  WorkspaceWithRole,
  WorkspaceMember,
  WorkspaceInvitation,
  CreateWorkspaceRequest,
  UpdateWorkspaceRequest,
  InviteToWorkspaceRequest,
  UpdateWorkspaceMemberRequest,
  WorkspaceMemberRole,
} from '@budgira/shared';
import { workspaceService } from '../services/workspace.service';
import { localizeError } from '../utils/errorMapper';

interface PendingInvitation extends WorkspaceInvitation {
  workspaceName: string;
}

interface WorkspaceState {
  // State
  workspaces: WorkspaceWithRole[];
  currentWorkspace: WorkspaceWithRole | null;
  members: WorkspaceMember[];
  invitations: WorkspaceInvitation[];
  pendingInvitations: PendingInvitation[];
  isLoading: boolean;
  error: string | null;

  // Actions - Workspaces
  fetchWorkspaces: () => Promise<void>;
  fetchCurrentWorkspace: () => Promise<void>;
  createWorkspace: (data: CreateWorkspaceRequest) => Promise<WorkspaceWithRole | null>;
  updateWorkspace: (id: string, data: UpdateWorkspaceRequest) => Promise<WorkspaceWithRole | null>;
  deleteWorkspace: (id: string) => Promise<boolean>;
  switchWorkspace: (workspaceId: string) => Promise<boolean>;

  // Actions - Members
  fetchMembers: (workspaceId: string) => Promise<void>;
  updateMember: (workspaceId: string, memberId: string, data: UpdateWorkspaceMemberRequest) => Promise<boolean>;
  removeMember: (workspaceId: string, memberId: string) => Promise<boolean>;

  // Actions - Invitations
  fetchInvitations: (workspaceId: string) => Promise<void>;
  sendInvitation: (workspaceId: string, data: InviteToWorkspaceRequest) => Promise<boolean>;
  cancelInvitation: (workspaceId: string, invitationId: string) => Promise<boolean>;
  fetchPendingInvitations: () => Promise<void>;
  acceptInvitation: (token: string) => Promise<WorkspaceWithRole | null>;

  // Utility
  clearError: () => void;
  reset: () => void;

  // Computed helpers
  canManageWorkspace: (workspaceId?: string) => boolean;
  canInviteToWorkspace: (workspaceId?: string) => boolean;
  getUserRole: (workspaceId?: string) => WorkspaceMemberRole | null;
}

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set, get) => ({
      workspaces: [],
      currentWorkspace: null,
      members: [],
      invitations: [],
      pendingInvitations: [],
      isLoading: false,
      error: null,

      // ===== Workspaces =====

      fetchWorkspaces: async (): Promise<void> => {
        set({ isLoading: true, error: null });
        try {
          const response = await workspaceService.list();
          if (response.success && response.data) {
            set({ workspaces: response.data, isLoading: false });
          } else {
            set({ error: response.error?.message || 'Failed to fetch workspaces', isLoading: false });
          }
        } catch (err) {
          const message = localizeError(err);
          set({ error: message, isLoading: false });
        }
      },

      fetchCurrentWorkspace: async (): Promise<void> => {
        set({ isLoading: true, error: null });
        try {
          const response = await workspaceService.getCurrent();
          if (response.success && response.data) {
            set({ currentWorkspace: response.data, isLoading: false });
          } else {
            set({ error: response.error?.message || 'Failed to fetch current workspace', isLoading: false });
          }
        } catch (err) {
          const message = localizeError(err);
          set({ error: message, isLoading: false });
        }
      },

      createWorkspace: async (data: CreateWorkspaceRequest): Promise<WorkspaceWithRole | null> => {
        set({ isLoading: true, error: null });
        try {
          const response = await workspaceService.create(data);
          if (response.success && response.data) {
            // Refetch workspaces to get the full list with roles
            await get().fetchWorkspaces();
            const newWorkspace = get().workspaces.find(w => w.id === response.data!.id);
            set({ isLoading: false });
            return newWorkspace || null;
          } else {
            set({ error: response.error?.message || 'Failed to create workspace', isLoading: false });
            return null;
          }
        } catch (err) {
          const message = localizeError(err);
          set({ error: message, isLoading: false });
          return null;
        }
      },

      updateWorkspace: async (id: string, data: UpdateWorkspaceRequest): Promise<WorkspaceWithRole | null> => {
        set({ isLoading: true, error: null });
        try {
          const response = await workspaceService.update(id, data);
          if (response.success && response.data) {
            set((state) => {
              return {
                workspaces: state.workspaces.map((w) =>
                  w.id === id ? { ...w, ...response.data } : w
                ),
                currentWorkspace:
                  state.currentWorkspace?.id === id
                    ? { ...state.currentWorkspace, ...response.data }
                    : state.currentWorkspace,
                isLoading: false,
              };
            });
            return get().workspaces.find(w => w.id === id) || null;
          } else {
            set({ error: response.error?.message || 'Failed to update workspace', isLoading: false });
            return null;
          }
        } catch (err) {
          const message = localizeError(err);
          set({ error: message, isLoading: false });
          return null;
        }
      },

      deleteWorkspace: async (id: string): Promise<boolean> => {
        set({ isLoading: true, error: null });
        try {
          const response = await workspaceService.delete(id);
          if (response.success) {
            set((state) => ({
              workspaces: state.workspaces.filter((w) => w.id !== id),
              currentWorkspace: state.currentWorkspace?.id === id ? null : state.currentWorkspace,
              isLoading: false,
            }));
            // If we deleted the current workspace, switch to the default one
            const { currentWorkspace, workspaces } = get();
            if (!currentWorkspace && workspaces.length > 0) {
              const defaultWorkspace = workspaces.find(w => w.isDefault) || workspaces[0];
              if (defaultWorkspace) {
                await get().switchWorkspace(defaultWorkspace.id);
              }
            }
            return true;
          } else {
            set({ error: response.error?.message || 'Failed to delete workspace', isLoading: false });
            return false;
          }
        } catch (err) {
          const message = localizeError(err);
          set({ error: message, isLoading: false });
          return false;
        }
      },

      switchWorkspace: async (workspaceId: string): Promise<boolean> => {
        set({ isLoading: true, error: null });
        try {
          const response = await workspaceService.switchWorkspace(workspaceId);
          if (response.success && response.data) {
            set({ currentWorkspace: response.data.workspace, isLoading: false });
            
            // Update sharedAccess in authStore
            // Import dynamically to avoid circular dependency
            const { useAuthStore } = await import('./authStore');
            useAuthStore.getState().updateSharedAccess(response.data.sharedAccess);
            
            return true;
          } else {
            set({ error: response.error?.message || 'Failed to switch workspace', isLoading: false });
            return false;
          }
        } catch (err) {
          const message = localizeError(err);
          set({ error: message, isLoading: false });
          return false;
        }
      },

      // ===== Members =====

      fetchMembers: async (workspaceId: string): Promise<void> => {
        set({ isLoading: true, error: null });
        try {
          const response = await workspaceService.getMembers(workspaceId);
          if (response.success && response.data) {
            set({ members: response.data, isLoading: false });
          } else {
            set({ error: response.error?.message || 'Failed to fetch members', isLoading: false });
          }
        } catch (err) {
          const message = localizeError(err);
          set({ error: message, isLoading: false });
        }
      },

      updateMember: async (
        workspaceId: string,
        memberId: string,
        data: UpdateWorkspaceMemberRequest
      ): Promise<boolean> => {
        set({ isLoading: true, error: null });
        try {
          const response = await workspaceService.updateMember(workspaceId, memberId, data);
          if (response.success && response.data) {
            set((state) => ({
              members: state.members.map((m) =>
                m.id === memberId ? { ...m, ...response.data } : m
              ),
              isLoading: false,
            }));
            return true;
          } else {
            set({ error: response.error?.message || 'Failed to update member', isLoading: false });
            return false;
          }
        } catch (err) {
          const message = localizeError(err);
          set({ error: message, isLoading: false });
          return false;
        }
      },

      removeMember: async (workspaceId: string, memberId: string): Promise<boolean> => {
        set({ isLoading: true, error: null });
        try {
          const response = await workspaceService.removeMember(workspaceId, memberId);
          if (response.success) {
            set((state) => ({
              members: state.members.filter((m) => m.id !== memberId),
              isLoading: false,
            }));
            return true;
          } else {
            set({ error: response.error?.message || 'Failed to remove member', isLoading: false });
            return false;
          }
        } catch (err) {
          const message = localizeError(err);
          set({ error: message, isLoading: false });
          return false;
        }
      },

      // ===== Invitations =====

      fetchInvitations: async (workspaceId: string): Promise<void> => {
        set({ isLoading: true, error: null });
        try {
          const response = await workspaceService.getInvitations(workspaceId);
          if (response.success && response.data) {
            set({ invitations: response.data, isLoading: false });
          } else {
            set({ error: response.error?.message || 'Failed to fetch invitations', isLoading: false });
          }
        } catch (err) {
          const message = localizeError(err);
          set({ error: message, isLoading: false });
        }
      },

      sendInvitation: async (
        workspaceId: string,
        data: InviteToWorkspaceRequest
      ): Promise<boolean> => {
        set({ isLoading: true, error: null });
        try {
          const response = await workspaceService.sendInvitation(workspaceId, data);
          if (response.success && response.data) {
            set((state) => ({
              invitations: [...state.invitations, response.data!],
              isLoading: false,
            }));
            return true;
          } else {
            set({ error: response.error?.message || 'Failed to send invitation', isLoading: false });
            return false;
          }
        } catch (err) {
          const message = localizeError(err);
          set({ error: message, isLoading: false });
          return false;
        }
      },

      cancelInvitation: async (workspaceId: string, invitationId: string): Promise<boolean> => {
        set({ isLoading: true, error: null });
        try {
          const response = await workspaceService.cancelInvitation(workspaceId, invitationId);
          if (response.success) {
            set((state) => ({
              invitations: state.invitations.filter((i) => i.id !== invitationId),
              isLoading: false,
            }));
            return true;
          } else {
            set({ error: response.error?.message || 'Failed to cancel invitation', isLoading: false });
            return false;
          }
        } catch (err) {
          const message = localizeError(err);
          set({ error: message, isLoading: false });
          return false;
        }
      },

      fetchPendingInvitations: async (): Promise<void> => {
        try {
          const response = await workspaceService.getPendingInvitations();
          if (response.success && response.data) {
            set({ pendingInvitations: response.data });
          }
        } catch {
          // Silently fail for pending invitations check
        }
      },

      acceptInvitation: async (token: string): Promise<WorkspaceWithRole | null> => {
        set({ isLoading: true, error: null });
        try {
          const response = await workspaceService.acceptInvitation(token);
          if (response.success && response.data) {
            const { workspace } = response.data;
            // Refetch workspaces to update the list
            await get().fetchWorkspaces();
            // Remove from pending
            set((state) => ({
              pendingInvitations: state.pendingInvitations.filter(
                (i) => i.token !== token
              ),
              isLoading: false,
            }));
            // Return the workspace with role
            return get().workspaces.find(w => w.id === workspace.id) || null;
          } else {
            set({ error: response.error?.message || 'Failed to accept invitation', isLoading: false });
            return null;
          }
        } catch (err) {
          const message = localizeError(err);
          set({ error: message, isLoading: false });
          return null;
        }
      },

      // ===== Utility =====

      clearError: () => set({ error: null }),

      reset: () =>
        set({
          workspaces: [],
          currentWorkspace: null,
          members: [],
          invitations: [],
          pendingInvitations: [],
          isLoading: false,
          error: null,
        }),

      // ===== Computed helpers =====

      canManageWorkspace: (workspaceId?: string): boolean => {
        const { currentWorkspace, workspaces } = get();
        const workspace = workspaceId
          ? workspaces.find((w) => w.id === workspaceId)
          : currentWorkspace;
        if (!workspace) return false;
        return workspace.role === 'owner' || workspace.role === 'admin';
      },

      canInviteToWorkspace: (workspaceId?: string): boolean => {
        const { currentWorkspace, workspaces } = get();
        const workspace = workspaceId
          ? workspaces.find((w) => w.id === workspaceId)
          : currentWorkspace;
        if (!workspace) return false;
        return workspace.role === 'owner' || workspace.role === 'admin';
      },

      getUserRole: (workspaceId?: string): WorkspaceMemberRole | null => {
        const { currentWorkspace, workspaces } = get();
        const workspace = workspaceId
          ? workspaces.find((w) => w.id === workspaceId)
          : currentWorkspace;
        return workspace?.role || null;
      },
    }),
    {
      name: 'budgira-workspace',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        currentWorkspace: state.currentWorkspace,
      }),
    }
  )
);

export default useWorkspaceStore;
